import { useState, useEffect } from 'react'
import { useDrawer } from '@/components/ui/drawer'
import { DrawerSkeleton } from '@/components/ui/drawer/DrawerSkeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { 
  usersService, 
  type User,
  getUserRoleLabel,
  getUserStatusLabel,
  getUserRoleColor,
  getUserStatusColor
} from '@/services/users'
import { tenantsService, type Tenant } from '@/services/tenants'
import { toast } from 'sonner'
import { 
  User as UserIcon, 
  Mail, 
  Phone, 
  Shield, 
  Building2, 
  Package, 
  CheckCircle, 
  AlertCircle,
  Activity,
  Clock,
  Edit,
  UserX,
  UserCheck,
  ChevronRight,
  Globe,
  Settings
} from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { format, formatDistanceToNow } from 'date-fns'
import { DRAWER_PRESETS } from '@/components/ui/drawer'
import { UserFormDrawer, UserFormFooter } from './UserFormDrawer'

interface UserViewDrawerProps {
  userId: string
  onClose?: () => void
}

interface UserActivity {
  id: string
  action: string
  description: string
  timestamp: string
  ip_address?: string
  user_agent?: string
  metadata?: Record<string, unknown>
}

export function UserViewDrawer({ userId }: UserViewDrawerProps) {
  const { openDrawer } = useDrawer()
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [activities, setActivities] = useState<UserActivity[]>([])
  const [permissions, setPermissions] = useState<string[]>([])
  
  const loadUserData = async () => {
    setLoading(true)
    
    try {
      // Load user data
      const userData = await usersService.get(userId)
      setUser(userData)
      
      // Load tenant data if user has tenant
      if (userData.tenant_id) {
        try {
          const tenantData = await tenantsService.get(userData.tenant_id)
          setTenant(tenantData)
        } catch (error) {
          console.warn('Failed to load tenant data:', error)
        }
      }
      
      // Load user permissions
      try {
        const userPermissions = await usersService.getPermissions(userId)
        setPermissions(userPermissions)
      } catch (error) {
        console.warn('Failed to load permissions:', error)
      }
      
      // Load user activity
      try {
        const userActivity = await usersService.getUserActivity(userId)
        setActivities((userActivity as UserActivity[]).slice(0, 10)) // Show last 10 activities
      } catch (error) {
        console.warn('Failed to load activity:', error)
      }
    } catch (error) {
      console.error('Failed to load user data:', error)
      toast.error('Failed to load user details')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUserData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const handleEditUser = () => {
    if (!user) return
    
    const drawerId = `user-form-${user.id}`
    openDrawer({
      ...DRAWER_PRESETS.form,
      id: drawerId,
      title: `Edit User: ${user.full_name}`,
      content: <UserFormDrawer userId={user.id} onSuccess={loadUserData} />,
      footer: <UserFormFooter isEditMode={true} drawerId={drawerId} userId={user.id} />,
      size: 'lg',
    })
  }

  const handleResetPassword = async () => {
    if (!user) return
    
    try {
      await usersService.resetPassword(user.id)
      toast.success(`Password reset link sent to ${user.email}`)
    } catch {
      toast.error('Failed to reset password')
    }
  }

  const handleToggleStatus = async () => {
    if (!user) return
    
    try {
      if (user.status === 'suspended') {
        await usersService.activate(user.id)
        toast.success(`User ${user.full_name} has been activated`)
      } else {
        await usersService.suspend(user.id)
        toast.success(`User ${user.full_name} has been suspended`)
      }
      loadUserData()
    } catch {
      toast.error('Failed to update user status')
    }
  }

  const handleImpersonate = async () => {
    if (!user) return
    
    try {
      const response = await usersService.impersonate(user.id)
      
      // Store the impersonation token
      localStorage.setItem('impersonation_token', response.token)
      
      // Redirect to joules-web with the token
      const joulesWebUrl = import.meta.env.VITE_JOULES_WEB_URL || 'http://localhost:5174'
      window.open(`${joulesWebUrl}/impersonate?token=${response.token}`, '_blank')
      
      toast.success(`Impersonating ${user.full_name}`)
    } catch {
      toast.error('Failed to impersonate user')
    }
  }

  if (loading) {
    return <DrawerSkeleton sections={5} />
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">User not found</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* User Header */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20 border-4 border-background shadow-lg">
              <AvatarImage src={user.avatar_url} alt={user.full_name} />
              <AvatarFallback className="text-2xl">
                {user.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-2xl font-bold">{user.full_name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{user.email}</span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant={getUserRoleColor(user.role) as 'default' | 'secondary' | 'destructive' | 'outline'}>
                  <Shield className="mr-1 h-3 w-3" />
                  {getUserRoleLabel(user.role)}
                </Badge>
                <Badge variant={getUserStatusColor(user.status) as 'default' | 'secondary' | 'destructive' | 'outline'}>
                  {getUserStatusLabel(user.status)}
                </Badge>
                {user.email_verified ? (
                  <Badge variant="outline" className="text-green-600">
                    <CheckCircle className="mr-1 h-3 w-3" />
                    Verified
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-yellow-600">
                    <AlertCircle className="mr-1 h-3 w-3" />
                    Unverified
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleEditUser}>
              <Edit className="mr-1 h-4 w-4" />
              Edit
            </Button>
            <Button variant="outline" size="sm" onClick={handleResetPassword}>
              <Edit className="mr-1 h-4 w-4" />
              Reset Password
            </Button>
            {user.role !== 'super_admin' && (
              <Button variant="outline" size="sm" onClick={handleImpersonate}>
                <Activity className="mr-1 h-4 w-4" />
                Impersonate
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleToggleStatus}
              className={user.status === 'suspended' ? 'text-green-600' : 'text-destructive'}
            >
              {user.status === 'suspended' ? (
                <>
                  <UserCheck className="mr-1 h-4 w-4" />
                  Activate
                </>
              ) : (
                <>
                  <UserX className="mr-1 h-4 w-4" />
                  Suspend
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <UserIcon className="h-5 w-5 text-primary" />
          Contact Information
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Email Address</p>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{user.email}</span>
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Phone Number</p>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{user.phone || 'Not provided'}</span>
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Organization & Access */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          Organization & Access
        </h3>
        
        {tenant ? (
          <div className="rounded-lg border bg-muted/50 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{tenant.company_name}</p>
                <p className="text-sm text-muted-foreground">{tenant.subdomain}.joules.app</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline">
                    {tenant.subscription_plan} Plan
                  </Badge>
                  <Badge variant={tenant.status === 'active' ? 'secondary' : 'outline'}>
                    {tenant.status}
                  </Badge>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
        ) : (
          <div className="rounded-lg border bg-muted/50 p-4">
            <p className="text-muted-foreground">No tenant organization assigned</p>
            <p className="text-sm text-muted-foreground mt-1">
              This user has platform-level access only
            </p>
          </div>
        )}
      </div>

      {/* Permissions */}
      {permissions.length > 0 && (
        <>
          <Separator />
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Custom Permissions
            </h3>
            <div className="flex flex-wrap gap-2">
              {permissions.map((permission) => (
                <Badge key={permission} variant="secondary">
                  {permission}
                </Badge>
              ))}
            </div>
          </div>
        </>
      )}

      <Separator />

      {/* Activity History */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          Recent Activity
        </h3>
        
        {activities.length > 0 ? (
          <div className="space-y-3">
            {activities.map((activity) => (
              <div key={activity.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="mt-1">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{activity.action}</p>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {activity.description}
                  </p>
                  {activity.ip_address && (
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Globe className="h-3 w-3" />
                        {activity.ip_address}
                      </span>
                      {activity.user_agent && (
                        <span className="truncate max-w-xs">
                          {activity.user_agent}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">No recent activity recorded</p>
        )}
      </div>

      <Separator />

      {/* Account Details */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5 text-primary" />
          Account Details
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground">User ID</p>
              <p className="font-mono font-medium">{user.id}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Created</p>
              <p className="font-medium">
                {format(new Date(user.created_at), 'MMM d, yyyy h:mm a')}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Last Updated</p>
              <p className="font-medium">
                {format(new Date(user.updated_at), 'MMM d, yyyy h:mm a')}
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground">Last Login</p>
              <p className="font-medium">
                {user.last_login 
                  ? format(new Date(user.last_login), 'MMM d, yyyy h:mm a')
                  : 'Never'
                }
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Account Age</p>
              <p className="font-medium">
                {formatDistanceToNow(new Date(user.created_at))}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Email Verification</p>
              <p className="font-medium">
                {user.email_verified ? (
                  <span className="text-green-600">Verified</span>
                ) : (
                  <span className="text-yellow-600">Pending Verification</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Metadata */}
      {user.metadata && Object.keys(user.metadata).length > 0 && (
        <>
          <Separator />
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              Additional Metadata
            </h3>
            <div className="rounded-lg bg-muted/50 p-4">
              <pre className="text-xs font-mono">
                {JSON.stringify(user.metadata, null, 2)}
              </pre>
            </div>
          </div>
        </>
      )}
    </div>
  )
}